/**
 * As etapas do consumo de IA — de quem foi cada chamada.
 *
 * Espelho de `core.vocabulary.AI_USAGE_STAGES`, com paridade travada por
 * `tests/test_ai_vocabulary_parity.py` (lê este arquivo). A tupla é
 * `as const` e o mapa de rótulos é tipado sobre ela DE PROPÓSITO: chave a
 * menos ou a mais é erro de compilação, não uma linha crua na tela — que
 * foi exatamente como `prompt-tasks.ts` nasceu com três chaves erradas
 * num `Record<string, string>`.
 *
 * `theme` é SINGULAR e não é typo: é a grafia que a esteira grava desde a
 * primeira linha do `ai_usage_log`. O vocabulário de TAREFA
 * (`PROMPT_TASK_LABELS`) diz `themes`; são duas coisas — a tarefa que o
 * motor executa e a etapa que a linha de consumo registra.
 *
 * Duas famílias: a ESTEIRA de classificação (uma linha por post × marca ×
 * tarefa, com empresa) e as chamadas PAGAS POR PESSOA (com pessoa, e com
 * empresa quando a rota roda sob uma — assistente de configuração e
 * Versa; o Copiloto e o autofill seguem sem empresa). A tela agrupa por
 * família antes de agrupar por etapa.
 */

export const AI_STAGES = [
  "polarity",
  "role",
  "theme",
  "key_message",
  "copiloto",
  "copiloto_audio",
  "company_autofill",
  "company_config",
  "versa_analysis",
  "rule_review",
  "versa_sentinel",
  "versa_swot",
  "versa_peak",
  "mention_summary",
  "versa_link_assistant"
] as const;

export type AiStage = (typeof AI_STAGES)[number];

export type AiStageFamily = "pipeline" | "personal";

export const AI_STAGE_LABELS: Record<AiStage, string> = {
  polarity: "Polaridade",
  role: "Papel da marca (abordagem)",
  theme: "Subtemas",
  key_message: "Mensagem-chave",
  copiloto: "Mestra Help — chat",
  copiloto_audio: "Mestra Help — áudio",
  company_autofill: "Autofill de empresa",
  company_config: "Assistente de configuração",
  versa_analysis: "Versa — análise",
  rule_review: "Consultoria de regras",
  versa_sentinel: "Versa — Sentinela",
  versa_swot: "Versa — análise SWOT",
  versa_peak: "Versa — análise de picos",
  mention_summary: "Resumo da menção",
  versa_link_assistant: "Versa — assistente do link"
};

export const AI_STAGE_FAMILY: Record<AiStage, AiStageFamily> = {
  polarity: "pipeline",
  role: "pipeline",
  theme: "pipeline",
  key_message: "pipeline",
  copiloto: "personal",
  copiloto_audio: "personal",
  company_autofill: "personal",
  company_config: "personal",
  versa_analysis: "personal",
  rule_review: "personal",
  versa_sentinel: "personal",
  versa_swot: "personal",
  versa_peak: "personal",
  mention_summary: "personal",
  versa_link_assistant: "personal"
};

export const AI_STAGE_FAMILY_LABELS: Record<AiStageFamily, string> = {
  pipeline: "Esteira de classificação",
  personal: "Chamadas pagas por pessoa"
};

export function isAiStage(value: string): value is AiStage {
  return (AI_STAGES as readonly string[]).includes(value);
}

/** Rótulo de uma etapa. Valor desconhecido cai no identificador cru —
 *  preferível a sumir da lista, mesma convenção de `promptTaskLabel`. */
export function aiStageLabel(stage: string): string {
  return isAiStage(stage) ? AI_STAGE_LABELS[stage] : stage;
}

/** Família de uma etapa, ou `null` pra valor que o vocabulário não
 *  conhece — a tela o mostra fora das duas famílias em vez de o esconder. */
export function aiStageFamily(stage: string): AiStageFamily | null {
  return isAiStage(stage) ? AI_STAGE_FAMILY[stage] : null;
}
